// src/components/Navbar.jsx
import { useEffect, useState } from "react";
import { Bars3Icon, UserIcon, ShoppingBagIcon } from "@heroicons/react/24/outline";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/slices/Users/authSlice";
import LanguageSwitcher from "./LanguageSwitcher";
import Sidebar from "./Sidebar";
import { PUBLIC_NAV_ITEMS, ACCOUNT_NAV_ITEMS, INSTAGRAM_URL } from "./navItems";
import logo from './logo.jpeg'; // adjust extension if needed

const desktopLinkClass = ({ isActive }) =>
  `relative text-sm font-semibold transition-colors ${
    isActive
      ? "text-white after:absolute after:-bottom-2 after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-[#EFE6DD]"
      : "text-[#EFE6DD] hover:text-[#EFE6DD]/70"
  }`;

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Redux state (your auth slice)
  const { user, token } = useSelector((state) => state.auth);
  const isLoggedIn = !!token;
  const isAdmin = isLoggedIn && user?.isAdmin === true;

  // Cierra el menú si la ruta cambia por cualquier otro medio
  useEffect(() => setMobileMenuOpen(false), [pathname]);

  const logoutHandler = () => {
    dispatch(logout());
    navigate("/");
  };

  const desktopItems = isLoggedIn ? [...PUBLIC_NAV_ITEMS, ...ACCOUNT_NAV_ITEMS] : PUBLIC_NAV_ITEMS;

  return (
    <div className="sticky top-0 z-30">
      <Sidebar
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onLogout={logoutHandler}
      />

      <header className="relative">
        <nav aria-label="Top">
          {/* Top bar (desktop) - shown only for guests */}
          {!isLoggedIn && (
            <div className="hidden bg-[#7A0001] lg:block">
              <div className="mx-auto flex h-10 max-w-7xl items-center justify-end space-x-6 px-8">
                <Link
                  to="/register"
                  className="text-sm font-medium text-[#EFE6DD] hover:text-[#EFE6DD]/70"
                >
                  {t("navbar.createAccount")}
                </Link>
                <span className="h-6 w-px bg-[#EFE6DD]/20" aria-hidden="true" />
                <Link
                  to="/login"
                  className="text-sm font-medium text-[#EFE6DD] hover:text-[#EFE6DD]/70"
                >
                  {t("navbar.signIn")}
                </Link>
              </div>
            </div>
          )}

          {/* Main navigation */}
          <div className="bg-[#9A0002]/95 shadow-md backdrop-blur-md supports-[backdrop-filter]:bg-[#9A0002]/90 pt-[env(safe-area-inset-top)]">
            <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
              <div className="relative flex h-16 items-center justify-between">
                {/* Desktop logo */}
                <div className="hidden lg:flex lg:items-center">
                  <Link to="/" className="flex items-center">
                    <span className="sr-only">Hotel Macarios</span>
                    <div className="relative h-12 w-12 overflow-hidden rounded-full border-2 border-[#EFE6DD] bg-white">
                     <img className="h-14 w-auto object-contain object-top" src={logo} alt="Hotel Macarios" />
                    </div>
                    <span className="ml-3 text-xl font-bold text-[#EFE6DD]">Hotel Macarios</span>
                  </Link>
                </div>

                {/* Mobile menu button */}
                <div className="flex flex-1 items-center lg:hidden">
                  <button
                    type="button"
                    className="flex h-11 w-11 items-center justify-center rounded-full text-[#EFE6DD] transition hover:bg-[#7A0001] active:scale-95"
                    onClick={() => setMobileMenuOpen(true)}
                  >
                    <span className="sr-only">{t("navbar.openMenu")}</span>
                    <Bars3Icon className="h-7 w-7" aria-hidden="true" />
                  </button>
                </div>

                {/* Centered mobile logo */}
                <Link
                  to="/"
                  className="absolute left-1/2 flex -translate-x-1/2 items-center gap-2 lg:hidden"
                >
                  <div className="relative h-11 w-11 overflow-hidden rounded-full border-2 border-[#EFE6DD] bg-white">
                    <img className="h-12 w-auto object-contain object-top" src={logo} alt="Hotel Macarios" />
                  </div>
                  <span className="font-['Fraunces'] text-lg font-semibold text-[#EFE6DD] min-[380px]:inline hidden">
                    Macarios
                  </span>
                </Link>

                {/* Mobile quick actions */}
                <div className="flex flex-1 items-center justify-end gap-1 lg:hidden">
                  <Link
                    to="/store"
                    className="flex h-11 w-11 items-center justify-center rounded-full text-[#EFE6DD] transition hover:bg-[#7A0001] active:scale-95"
                  >
                    <span className="sr-only">{t("navbar.store")}</span>
                    <ShoppingBagIcon className="h-6 w-6" aria-hidden="true" />
                  </Link>
                  <Link
                    to={isLoggedIn ? "/profile" : "/login"}
                    className="flex h-11 w-11 items-center justify-center rounded-full text-[#EFE6DD] transition hover:bg-[#7A0001] active:scale-95"
                  >
                    <span className="sr-only">{isLoggedIn ? t("navbar.myProfile") : t("navbar.signIn")}</span>
                    <UserIcon className="h-6 w-6" aria-hidden="true" />
                  </Link>
                </div>

                {/* Desktop navigation links */}
                <div className="hidden lg:flex lg:flex-1 lg:justify-center lg:gap-x-8">
                  {desktopItems.map((item) => (
                    <NavLink key={item.to} to={item.to} className={desktopLinkClass}>
                      {t(item.labelKey) || item.fallback}
                    </NavLink>
                  ))}
                </div>

                {/* Desktop right section */}
                <div className="hidden lg:flex flex-1 items-center justify-end">
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="inline-flex items-center rounded-md border border-transparent bg-[#EFE6DD] px-4 py-2 text-sm font-medium text-[#9A0002] shadow-sm hover:bg-white mr-4"
                    >
                      {t("navbar.adminDashboard")}
                    </Link>
                  )}

                  <div className="flex items-center lg:ml-8">
                    <div className="flex space-x-8">
                      {isLoggedIn && (
                        <div className="flex">
                          <Link to="/profile" className="-m-2 p-2 mr-2 text-[#EFE6DD] hover:text-[#EFE6DD]/70">
                            <span className="sr-only">{t("navbar.myProfile")}</span>
                            <UserIcon className="h-6 w-6" />
                          </Link>
                          <button onClick={logoutHandler} className="text-[#EFE6DD] hover:text-[#EFE6DD]/70">
                            <span className="sr-only">{t("navbar.signOut")}</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>

                    <span className="mx-4 h-6 w-px bg-[#EFE6DD]/20 lg:mx-6" aria-hidden="true" />

                    {/* Instagram link */}
                    <div className="flow-root">
                      <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group -m-2 flex items-center p-2"
                      >
                        <span className="sr-only">Instagram</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-6 w-6 flex-shrink-0 text-[#EFE6DD] group-hover:text-[#EFE6DD]/70"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17.25 6.75L17.25 6.751M7.5 3.75H16.5C18.1569 3.75 19.5 5.09315 19.5 6.75V17.25C19.5 18.9069 18.1569 20.25 16.5 20.25H7.5C5.84315 20.25 4.5 18.9069 4.5 17.25V6.75C4.5 5.09315 5.84315 3.75 7.5 3.75ZM12 15.75C9.92893 15.75 8.25 14.0711 8.25 12C8.25 9.92893 9.92893 8.25 12 8.25C14.0711 8.25 15.75 9.92893 15.75 12C15.75 14.0711 14.0711 15.75 12 15.75ZM17.625 6.375C17.625 6.78921 17.2892 7.125 16.875 7.125C16.4608 7.125 16.125 6.78921 16.125 6.375C16.125 5.96079 16.4608 5.625 16.875 5.625C17.2892 5.625 17.625 5.96079 17.625 6.375Z"
                          />
                        </svg>
                      </a>
                    </div>

                    <span className="mx-4 h-6 w-px bg-[#EFE6DD]/20 lg:mx-6" aria-hidden="true" />

                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </div>
  );
}
